import { Link } from "react-router-dom";
import { Warehouse, Trophy, AlertTriangle } from "lucide-react";
import { useApp } from "@/context/AppContext";
import { fmtBay } from "./SvcBits";

const TH = "px-2.5 py-2 text-[10px] uppercase tracking-wider font-semibold text-muted-foreground whitespace-nowrap";

function GroupCard({ g, k }) {
  return (
    <div className="rounded-md border bg-card p-3 min-w-[200px] flex-1" data-testid={`bay-group-${k}`}>
      <div className="text-[11px] uppercase tracking-wider text-muted-foreground font-semibold">{g.label}</div>
      <div className="font-heading text-2xl font-extrabold num mt-0.5" data-testid={`bay-group-${k}-value`}>{fmtBay(g.per_bay_day)}</div>
      <div className="text-[11px] text-muted-foreground num">{g.ros} ROs ÷ {g.bay_days} bay-days · {g.workshops} workshop{g.workshops === 1 ? "" : "s"} · {g.bays} bays today{g.best != null && <> · best <b className="text-foreground">{fmtBay(g.best)}</b> {g.best_code}</>}</div>
    </div>
  );
}

export function UnsetNotice({ unset, compact = false }) {
  const { user } = useApp();
  if (!unset?.length) return null;
  return (
    <div className={`rounded-md border border-amber-200 bg-amber-50 text-amber-900 flex flex-wrap items-center gap-2 ${compact ? "px-3 py-1.5 text-[11px]" : "px-4 py-2 text-xs"}`} data-testid="bay-unset-notice">
      <AlertTriangle size={13} /> Bays not captured for {unset.length} workshop{unset.length === 1 ? "" : "s"}: <span className="num">{unset.join(", ")}</span>
      {user?.role === "admin" ? <Link to="/admin?tab=dealers" className="underline font-semibold" data-testid="bay-unset-admin-link">Set GS / BP bays in Admin › Dealers</Link> : <span>— ask Admin to record GS / BP bays under Dealers.</span>}
    </div>
  );
}

// Compact strip for the Command Center: group GS / BP productivity + who is missing bays.
export function BayStrip({ bays, unset, definition }) {
  if (!bays) return null;
  return (
    <div className="space-y-2" data-testid="svc-bay-strip">
      <div className="flex flex-wrap items-stretch gap-2">
        <div className="rounded-md border bg-card p-3 flex items-center gap-2 text-xs min-w-[180px]"><Warehouse size={16} /><div><div className="font-heading font-semibold">Bay productivity</div><div className="text-[10px] text-muted-foreground">ROs invoiced per bay per active day</div></div></div>
        <GroupCard g={bays.gs} k="gs" />
        <GroupCard g={bays.bp} k="bp" />
      </div>
      <UnsetNotice unset={unset} compact />
      {definition && <p className="text-[10px] text-muted-foreground">{definition}</p>}
    </div>
  );
}

// Full table: every workshop's GS / BP bays, ROs, active days and ROs/bay/day with rank.
export function BayPanel({ bays, title = "H · Bay productivity", showGroup = true }) {
  if (!bays) return null;
  const rows = [...(bays.rows || [])].sort((a, b) => (a.gs_rank ?? 999) - (b.gs_rank ?? 999) || a.code.localeCompare(b.code));
  const heat = (rank, n) => (rank == null ? "" : rank === 1 ? "bg-emerald-600 text-white" : rank <= 3 ? "bg-emerald-100 text-emerald-900" : rank > n - 3 && n > 5 ? "bg-red-100 text-red-900" : "bg-secondary");
  const n = rows.filter((r) => r.gs_rank != null).length;
  const nb = rows.filter((r) => r.bp_rank != null).length;
  return (
    <div className="space-y-2" data-testid="bay-panel">
      {showGroup && (
        <div className="flex flex-wrap items-stretch gap-2">
          <GroupCard g={bays.group.gs} k="gs" />
          <GroupCard g={bays.group.bp} k="bp" />
        </div>
      )}
      <UnsetNotice unset={bays.unset} compact />
      <div className="border bg-card rounded-md overflow-auto">
        <div className="px-4 py-3 border-b flex items-center gap-2"><Trophy size={14} /><h3 className="font-heading text-sm font-semibold">{title}</h3><span className="text-[11px] text-muted-foreground">ranked on GS ROs/bay/day · day-weighted by the bays in force each active day</span></div>
        <table className="w-full text-xs">
          <thead className="border-b">
            <tr>
              <th className={`${TH} text-left`}>Workshop</th><th className={`${TH} text-left`}>Facility</th><th className={`${TH} text-right`}>Active days</th>
              <th className={`${TH} text-right border-l-2 border-border/80`}>GS bays</th><th className={`${TH} text-right`}>GS ROs</th><th className={`${TH} text-right`}>GS ROs/bay/day</th><th className={`${TH} text-center`}>Rank</th>
              <th className={`${TH} text-right border-l-2 border-border/80`}>BP bays</th><th className={`${TH} text-right`}>BP ROs</th><th className={`${TH} text-right`}>BP ROs/bay/day</th><th className={`${TH} text-center`}>Rank</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.code} className={`border-b last:border-0 hover:bg-muted/50 ${r.needs_bays ? "text-muted-foreground" : ""}`} data-testid={`bay-row-${r.code}`}>
                <td className="px-2.5 py-1.5 font-medium whitespace-nowrap"><Link to={`/service/workshop/${r.code}`} className="hover:underline">{r.code}</Link> <span className="text-muted-foreground font-normal">{r.name}</span>{r.needs_bays && <span className="ml-1.5 rounded bg-amber-50 text-amber-800 border border-amber-200 px-1 py-0.5 text-[9px]">bays not set</span>}</td>
                <td className="px-2.5 py-1.5">{r.facility}</td>
                <td className="px-2.5 py-1.5 text-right num">{r.active_days}</td>
                <td className="px-2.5 py-1.5 text-right num border-l-2 border-border/80">{r.gs_bays ?? "—"}</td>
                <td className="px-2.5 py-1.5 text-right num">{r.gs_ros}</td>
                <td className="px-2.5 py-1.5 text-right num font-bold" data-testid={`bay-row-${r.code}-gs`}>{fmtBay(r.gs)}</td>
                <td className="px-2.5 py-1.5 text-center">{r.gs_rank != null && <span className={`inline-flex h-5 min-w-5 px-1 items-center justify-center rounded text-[10px] font-bold ${heat(r.gs_rank, n)}`}>{r.gs_rank}</span>}</td>
                <td className="px-2.5 py-1.5 text-right num border-l-2 border-border/80">{r.bp_bays ?? "—"}</td>
                <td className="px-2.5 py-1.5 text-right num">{r.bp_ros}</td>
                <td className="px-2.5 py-1.5 text-right num font-bold" data-testid={`bay-row-${r.code}-bp`}>{fmtBay(r.bp)}</td>
                <td className="px-2.5 py-1.5 text-center">{r.bp_rank != null && <span className={`inline-flex h-5 min-w-5 px-1 items-center justify-center rounded text-[10px] font-bold ${heat(r.bp_rank, nb)}`}>{r.bp_rank}</span>}</td>
              </tr>
            ))}
            {rows.length === 0 && <tr><td colSpan={11} className="px-3 py-6 text-center text-muted-foreground">No workshops</td></tr>}
          </tbody>
          {showGroup && (
            <tfoot className="border-t bg-secondary/40 font-semibold">
              <tr data-testid="bay-group-row">
                <td className="px-2.5 py-1.5" colSpan={3}>Group · Σ ROs ÷ Σ bay-days</td>
                <td className="px-2.5 py-1.5 text-right num border-l-2 border-border/80">{bays.group.gs.bays}</td><td className="px-2.5 py-1.5 text-right num">{bays.group.gs.ros}</td><td className="px-2.5 py-1.5 text-right num">{fmtBay(bays.group.gs.per_bay_day)}</td><td />
                <td className="px-2.5 py-1.5 text-right num border-l-2 border-border/80">{bays.group.bp.bays}</td><td className="px-2.5 py-1.5 text-right num">{bays.group.bp.ros}</td><td className="px-2.5 py-1.5 text-right num">{fmtBay(bays.group.bp.per_bay_day)}</td><td />
              </tr>
            </tfoot>
          )}
        </table>
      </div>
      {bays.definition && <p className="text-[10px] text-muted-foreground">{bays.definition}</p>}
    </div>
  );
}
